import { AgreementVersion } from 'models/Agreement';
import { BillingInfo } from 'models/Billing';
import { serverUrl, getDefaultHeaders } from 'services/authService';

const parseErrorMessage = async (
  response: Response,
  fallback: string
): Promise<string> => {
  try {
    const errorData = await response.json();
    return errorData.message ?? errorData.detail ?? fallback;
  } catch {
    return fallback;
  }
};

const getBilling = async (accessToken: string): Promise<BillingInfo> => {
  const response = await fetch(`${serverUrl}/billing`, {
    headers: getDefaultHeaders(accessToken)
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Failed to fetch billing'));
  }

  return response.json();
};

const getAgreement = async (
  agreementType = 'service_agreement',
  version?: string
): Promise<AgreementVersion> => {
  const params = new URLSearchParams();
  if (version) {
    params.set('version', version);
  }
  const query = params.toString();
  const response = await fetch(
    `${serverUrl}/agreements/${encodeURIComponent(agreementType)}${
      query ? `?${query}` : ''
    }`
  );

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response, 'Failed to load agreement'));
  }

  return response.json();
};

const acceptAgreement = async (accessToken: string): Promise<BillingInfo | void> => {
  const response = await fetch(`${serverUrl}/billing/agreement-acceptance`, {
    method: 'POST',
    headers: getDefaultHeaders(accessToken),
    body: JSON.stringify({})
  });

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(response, 'Failed to accept billing agreement')
    );
  }

  // 201 may return the updated billing payload or an empty body
  const text = await response.text();
  if (!text) {
    return;
  }
  try {
    return JSON.parse(text) as BillingInfo;
  } catch {
    return;
  }
};

export { getBilling, getAgreement, acceptAgreement };
